import type { FlightDataSource, IFlightMonitorRow } from './types';

// Who reported a flight row's status (providers-by-service §8b, synthetic-airline
// §8b). Pure data reading — the board renders it (routes/flights/status.ts) and the
// WS merge guards with it (lib/ws/apply-event.ts), so both read it one way.
//
// The rule is the BFF overlay's (`operator-bff flight-airline-status.ts`), restated —
// an app never imports another app's source: the airline (`SERVICE_PROVIDER`) wins
// the status over every other source whatever the timestamps (owner, 2026-10-22);
// recency decides only between two airline observations; and «la aerolínea gana
// siempre, salvo que FlightAware informe una salida o llegada REAL, que cierra un
// «demorado» que quedó viejo» (owner, 2026-10-28) — FlightAware's DEPARTED / ARRIVED
// beats an airline SCHEDULED / DELAYED.

const FLIGHT_DATA_SOURCES: readonly FlightDataSource[] = [
  'SERVICE_PROVIDER',
  'PREDICTOR',
  'FILE',
  'SYNTHETIC',
];

/** One field's source + when that source observed the value, as read off the wire. */
export interface IReadSource {
  source: FlightDataSource;
  observedAt?: string;
}

/** A known source token (+ its time, when it is a non-empty string); null for anything else. */
export function readSource(raw: unknown, observedAt: unknown): IReadSource | null {
  if (typeof raw !== 'string' || !(FLIGHT_DATA_SOURCES as readonly string[]).includes(raw)) {
    return null;
  }
  return {
    source: raw as FlightDataSource,
    ...(typeof observedAt === 'string' && observedAt ? { observedAt } : {}),
  };
}

/** Epoch ms of an ISO instant; null when absent or unparseable. */
export function instantOf(iso: string | undefined): number | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : at;
}

/** Statuses that say the flight actually moved — a fact, not a plan. */
const MOVEMENT_STATUSES: readonly string[] = ['DEPARTED', 'ARRIVED'];

/** Statuses of a flight that has not moved yet — the ones a movement fact closes. */
const NOT_YET_STATUSES: readonly string[] = ['SCHEDULED', 'DELAYED'];

const statusIn = (status: string | undefined, set: readonly string[]): boolean =>
  typeof status === 'string' && set.includes(status.trim().toUpperCase());

/**
 * An actual departure or arrival FlightAware reported: a `PREDICTOR` status that
 * reads DEPARTED or ARRIVED (the predictor maps only FlightAware's `active` /
 * `landed` to them). A file or synthetic status reports no movement.
 */
function isMovementFact(source: FlightDataSource | undefined, status: string | undefined): boolean {
  return source === 'PREDICTOR' && statusIn(status, MOVEMENT_STATUSES);
}

/**
 * Who reported the status the row shows. A row is read with `provenance.status`;
 * a live `flight.status-changed` delta lands its source top-level (`statusSource` +
 * `statusObservedAt`) and leaves `provenance` as read (the WS merge is shallow).
 *
 * The airline wins the status (owner, 2026-10-22; precedence 2026-10-28) — the tier
 * first, then the time, as the BFF overlay decides it (`airlineStatusWins`):
 *   - one side is the airline's (`SERVICE_PROVIDER`) and the other is not ⇒ the
 *     airline's, whatever the two times say — a list read with a fresher FlightAware
 *     time never takes a live airline status back. Except «salvo que FlightAware
 *     informe una salida o llegada REAL» (owner, 2026-10-28): when the LIVE side is
 *     FlightAware's and the row reads DEPARTED / ARRIVED, that is the actual movement
 *     `statusDeltaFor` let close a not-yet airline status — it is the row's;
 *   - both the same tier ⇒ the NEWER observation wins; the live one also wins a tie,
 *     or when either side has no usable time: a delta is merged onto the row as read,
 *     so it is the later write.
 */
export function resolveStatusSource(
  row: Pick<IFlightMonitorRow, 'status' | 'provenance' | 'statusSource' | 'statusObservedAt'>,
): IReadSource | null {
  const read = row.provenance?.status;
  const loaded = read ? readSource(read.source, read.observedAt) : null;
  const live = readSource(row.statusSource, row.statusObservedAt);
  if (!live || !loaded) return live ?? loaded;
  const liveIsAirline = live.source === 'SERVICE_PROVIDER';
  if (liveIsAirline !== (loaded.source === 'SERVICE_PROVIDER')) {
    if (!liveIsAirline && isMovementFact(live.source, row.status)) return live;
    return liveIsAirline ? live : loaded;
  }
  const liveAt = instantOf(live.observedAt);
  const loadedAt = instantOf(loaded.observedAt);
  return liveAt !== null && loadedAt !== null && loadedAt > liveAt ? loaded : live;
}

/** The row fields a status delta writes: the status, its source, and what only means something beside it. */
const STATUS_DELTA_FIELDS: readonly (keyof IFlightMonitorRow)[] = [
  'status',
  'statusSource',
  'statusObservedAt',
  'delayMinutes',
  'estimatedDepartureUtc',
  'actualDepartureUtc',
];

/** The delta without its status fields — what merges when its status loses. */
function withoutStatus(delta: Partial<IFlightMonitorRow>): Partial<IFlightMonitorRow> {
  const kept: Partial<IFlightMonitorRow> = { ...delta };
  for (const field of STATUS_DELTA_FIELDS) delete kept[field];
  return kept;
}

/**
 * Whether a live delta's status may replace the row's — the rule of
 * `resolveStatusSource`, decided before the merge so the merged row names the
 * delta's source exactly when its status landed:
 *   - an airline delta over the airline's status: the newer wins (a tie, or either
 *     side without a usable time, goes to the delta — it is the later write);
 *   - an airline delta over another source's status: it wins, except over
 *     FlightAware's actual DEPARTED / ARRIVED when it says SCHEDULED / DELAYED;
 *   - any other delta (the predictor relay stamps `PREDICTOR`; an unstamped one
 *     claims nothing) over the airline's status: only FlightAware's DEPARTED /
 *     ARRIVED over a SCHEDULED / DELAYED. Once that movement holds a row READ with
 *     the airline's status, only FlightAware's movement follows it (DEPARTED →
 *     ARRIVED, or a stamp without a status) — anything else would read as the
 *     airline's again;
 *   - any other delta over a non-airline status, or a row with none: as sent.
 */
function takesStatus(
  row: IFlightMonitorRow,
  held: IReadSource | null,
  delta: Partial<IFlightMonitorRow>,
  incoming: IReadSource | null,
): boolean {
  if (incoming?.source === 'SERVICE_PROVIDER') {
    if (held?.source !== 'SERVICE_PROVIDER') {
      return !(
        isMovementFact(held?.source, row.status) && statusIn(delta.status, NOT_YET_STATUSES)
      );
    }
    const heldAt = instantOf(held.observedAt);
    const incomingAt = instantOf(incoming.observedAt);
    return heldAt === null || incomingAt === null || incomingAt >= heldAt;
  }
  if (held?.source === 'SERVICE_PROVIDER') {
    return isMovementFact(incoming?.source, delta.status) && statusIn(row.status, NOT_YET_STATUSES);
  }
  if (row.provenance?.status?.source === 'SERVICE_PROVIDER') {
    return isMovementFact(incoming?.source, delta.status ?? row.status);
  }
  return true;
}

/**
 * The part of a live `flight.status-changed` delta a row may take — invariant 4
 * (owner, 2026-10-22): the airline wins the status, and among airline observations
 * the newer one wins; an actual movement FlightAware reports ends a stale airline
 * SCHEDULED / DELAYED (owner, 2026-10-28) — the same rule as the BFF overlay;
 * `takesStatus` decides.
 *
 * While the row's status is the airline's, a delta that is not the airline's, or an
 * airline delta OLDER than the one held, loses its status fields
 * (`STATUS_DELTA_FIELDS`) — unless it is FlightAware's DEPARTED / ARRIVED over an
 * airline SCHEDULED / DELAYED, which is applied. While the row holds FlightAware's
 * DEPARTED / ARRIVED, an airline SCHEDULED / DELAYED delta loses its status fields;
 * the airline's CANCELLED / DIVERTED / DEPARTED / ARRIVED land. A delta that loses
 * its status still merges its other fields — the forecast (`cancelProbability`,
 * `expectedDelayMinutes`, `riskSource`, …) has its own source and never competes
 * with the airline's status. Every other case is the delta as sent.
 */
export function statusDeltaFor(
  row: IFlightMonitorRow,
  delta: Partial<IFlightMonitorRow>,
): Partial<IFlightMonitorRow> {
  const held = resolveStatusSource(row);
  const incoming = readSource(delta.statusSource, delta.statusObservedAt);
  return takesStatus(row, held, delta, incoming) ? delta : withoutStatus(delta);
}
